import React, { useState, useEffect } from "react";
import Content from "components/Content";
import { Box, TextField, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DataGrid from "./DataGrid";
import { useNavigate } from "react-router-dom";

function ListTicketComponent(props) {
  const { tickets, setTicketSelected, setShowAssignTicket, setShowMessageConfirmReAssign, setShowMessageConfirmReOpen, account } = props;
  const [rows, setRows] = useState([]);
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");

  const baseColumns = [
    {
      field: "id",
      headerName: "NRO TICKET",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <div
          className="swt-table-field-name"
          style={{ textTransform: "uppercase" }}
        >
          {params.value}
        </div>
      ),
    },
    {
      field: "claim",
      headerName: "RECLAMO ASOCIADO",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <div className="swt-table-field-name">{params.value}</div>
      ),
    },
    {
      field: "assigned",
      headerName: "ASIGNADO A",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Box
          className="swt-table-field-name"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            margin: "auto",
            width: "200px",
            height: "100%"
          }}>
          {params.value ? params.value.full_name : "S/A"}
        </Box>
      ),
    },
    {
      field: "status",
      headerName: "ESTADO DEL TICKET",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        // En la vista de tickets, el estado y el booleano viajan dentro del objeto params.value
        const statusText = params.value?.data;
        const hasNewInfo = params.value?.has_new_info;

        let bgColor = "transparent";
        let textColor = "inherit";

        if (statusText === "Pendiente Revisión") {
          bgColor = "#FFC107";
          textColor = "#5d4037";
        } else if (statusText === "En curso") {
          bgColor = "#4CAF50";
          textColor = "#ffffff";
        } else if (statusText === "Cerrado") {
          bgColor = "#2196F3";
          textColor = "#ffffff";
        }

        return (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", height: "100%" }}>
            <Box
              sx={{
                display: "inline-flex", // Contenedor flexible en línea
                alignItems: "center", // Centrado vertical de los elementos internos
                gap: "6px", // Espacio entre el texto y el punto rojo
                px: 1.5,
                py: 0.5,
                borderRadius: "12px",
                backgroundColor: bgColor,
                color: textColor,
                fontSize: "12px",
                fontWeight: "bold",
                textAlign: "center",
                boxShadow: "0px 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              {statusText}

              {/* El punto rojo anidado dentro del badge */}
              {hasNewInfo === true && (
                <Box
                  sx={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor: "#E30000",
                    border: "1px solid #ffffff", // Borde sutil para resaltar sobre el fondo
                    flexShrink: 0,
                    animation: "pulse 1.5s ease-in-out infinite",
                    "@keyframes pulse": {
                      "0%": {
                        opacity: 1,
                        boxShadow: "0 0 0 0 rgba(227, 0, 0, 0.4)",
                      },
                      "70%": {
                        opacity: 0.7,
                        boxShadow: "0 0 0 4px rgba(227, 0, 0, 0)", // Ajustado a 4px para que no desborde demasiado el badge
                      },
                      "100%": {
                        opacity: 1,
                        boxShadow: "0 0 0 0 rgba(227, 0, 0, 0)",
                      },
                    },
                  }}
                />
              )}
            </Box>
          </Box>
        );
      },
    },
    {
      field: "created_at",
      headerName: "FECHA DE CREACIÓN",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <div className="swt-table-field-name">{params.value}</div>
      ),
    },
  ];

  const columns = account?.roles?.[0]?.name === 'Admin'
    ? [...baseColumns.slice(0, 3), ...baseColumns.slice(3)]
    : baseColumns;

  const handleFilter = (e) => {
    const text = e.target.value;
    setSearchText(text);
  };

  const handleEdit = (id) => {
    navigate(`/tickets/${id}`);
  };

  useEffect(() => {
    let filtered = [...tickets];

    if (searchText.trim() !== "") {
      const query = searchText.toLowerCase();
      filtered = filtered.filter((ticket) => {
        const ticketIdMatch = ticket.id?.toLowerCase().includes(query);
        const assignedUserMatch = ticket.assigned?.full_name?.toLowerCase().includes(query);
        return ticketIdMatch || assignedUserMatch;
      });
    }

    setRows(filtered);
  }, [searchText, tickets]);

  return (
    <Content className="swt-dashboard" isLoaded="true">
      <div style={{ margin: "30px 100px", height: "calc(100% - 90px)" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ minWidth: "400px" }}>
            <TextField
              placeholder="Buscar nro de ticket o agente"
              InputProps={{
                endAdornment: (
                  <SearchIcon
                    sx={{
                      fill: "#A2A2BD",
                      marginRight: "8px",
                    }}
                  />
                ),
              }}
              sx={{
                m: 0,
                p: 0,
                marginBottom: "20px",
                backgroundColor: "#FFF",
                borderRadius: "15px",
                border: "1px solid rgba(61, 62, 64, 0.50)",
                background: "#fff",
                "& fieldset": {
                  borderRadius: "5px",
                },
                "& input": {
                  paddingY: "12px",
                  fontFamily: "Encode Sans",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "unset !important",
                },
                width: "100%",
                maxWidth: "400px",
              }}
              onChange={handleFilter}
            />
          </Box>
        </Box>
        <DataGrid
          rows={rows}
          columns={columns}
          handleEdit={handleEdit}
          setTicketSelected={setTicketSelected}
          setShowAssignTicket={setShowAssignTicket}
          setShowMessageConfirmReAssign={setShowMessageConfirmReAssign}
          setShowMessageConfirmReOpen={setShowMessageConfirmReOpen}
          backgroundColor={"#fff"}
          account={account}
          noDataMessage="No hay tickets para mostrar en este momento."
        />
      </div>
    </Content>
  );
}

export default ListTicketComponent;